// HOTFIX-1: the production on a phone (below 1024 px), in place of the Workbench. Read-only: the card's status and
// people, and each of its video assets (the current cut, then its archived versions) with a Download button. The download
// is the Workbench's own call, GET /api/video-assets/:id/download, behind the same gate (requireStaff + clientGate
// 'video-assets'), so a phone reaches nothing a desktop does not. Length and size come from GET /api/video-assets/:id
// (same gate); the card's lists (…?liveProductionId, …/archived) are the ones the Workbench already loads.
// No editing stage, scrub dock or cut list.
import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import api from '../../lib/api';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Skeleton from '../../ui/Skeleton';
import StageChip from '../../ui/StageChip';
import Tag from '../../ui/Tag';
import { useToast } from '../../ui/Toast';
import { clean } from '../../shell/copy';
import { ladderOf, openUrl } from './shared';

const fmtSize = b => { const n = Number(b); if (!n) return null; return n >= 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`; };
const fmtLength = s => { const n = Math.round(Number(s)); if (!n) return null; const h = Math.floor(n / 3600), m = Math.floor((n % 3600) / 60), sec = String(n % 60).padStart(2, '0'); return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`; };

export default function PhoneView({ record, status, isIncomplete, isPlanned, writerName, videoAsset, assetLoading, archivedVersions }) {
  const toast = useToast();
  const [facts, setFacts] = useState({});      // id → { durationSeconds, fileSizeBytes }
  const [busyId, setBusyId] = useState(null);
  const assets = [
    ...(videoAsset ? [{ id: videoAsset.id, fileName: videoAsset.fileName, archived: false }] : []),
    ...archivedVersions.map(v => ({ id: v.id, fileName: v.fileName, archived: true })),
  ];
  const ids = assets.map(a => a.id).join(',');
  useEffect(() => {
    if (!ids) return;
    ids.split(',').forEach(id => api.get(`/api/video-assets/${id}`)
      .then(r => setFacts(prev => ({ ...prev, [id]: { durationSeconds: r.data?.durationSeconds, fileSizeBytes: r.data?.fileSizeBytes } })))
      .catch(() => {}));
  }, [ids]);

  async function download(a) {
    setBusyId(a.id);
    try { const res = await api.get(`/api/video-assets/${a.id}/download`); openUrl(res.data.url); }
    catch { toast('The download did not start. Try again.', { tone: 'danger' }); }
    finally { setBusyId(null); }
  }

  const talent = record.resolvedTalent?.kind === 'booked' || record.resolvedTalent?.kind === 'mapped'
    ? clean(record.resolvedTalent.talents.map(t => t.name).join(', ')) : null;
  const person = v => (clean(v) ? clean(v) : <span className="ui-text-3">Not assigned</span>);

  return (
    <div className="ui ui-page ui-stack" data-testid="production-page" data-view="phone" data-status={status}>
      <div className="ui-cluster" data-testid="production-badges">
        <span data-testid="production-status" data-status={status}><StageChip stage={ladderOf(status)} /></span>
        {isIncomplete && <Badge condition="Incomplete" />}
        {status === 'Archived' && <Tag>Set aside</Tag>}
        {isPlanned && <Tag>Planned</Tag>}
        {record.isArchived && <Tag>Archived</Tag>}
      </div>
      <dl className="ui-kv" data-testid="phone-people">
        <dt>Client</dt><dd>{clean(record.client?.name) || <span className="ui-text-3">No client</span>}</dd>
        <dt>Talent</dt><dd>{talent || <span className="ui-text-3">Assigned at booking</span>}</dd>
        <dt>Writer</dt><dd>{record.script?.id ? person(writerName) : <span className="ui-text-3">Riff, no script</span>}</dd>
        <dt>Producer</dt><dd>{person(record.producer?.name)}{record.producerDefaulted && record.producer ? <span className="ui-text-3"> (defaulted)</span> : null}</dd>
        <dt>Shoot lead</dt><dd>{person(record.shootLead?.name)}</dd>
        <dt>Editor</dt><dd>{person(record.editor?.name)}</dd>
      </dl>
      <section className="ui-section" aria-label="Videos">
        <h2 className="ui-section__title">Videos</h2>
        {assetLoading ? <Skeleton variant="block" height={56} label="Loading the videos" />
          : assets.length === 0 ? <p className="ui-p ui-dense ui-text-3" data-testid="phone-no-video">No video uploaded yet.</p>
          : (
            <div data-testid="phone-videos">
              {assets.map(a => {
                const f = facts[a.id] || {};
                const meta = [a.archived ? 'Archived version' : 'Current cut', fmtLength(f.durationSeconds), fmtSize(f.fileSizeBytes)].filter(Boolean).join(' · ');
                return (
                  <div key={a.id} className="ui-row" data-testid="phone-video" data-asset={a.id}>
                    <span className="ui-row__main">
                      <span className="ui-row__title">{clean(a.fileName) || 'Video'}</span>
                      <span className="ui-row__meta">{meta}</span>
                    </span>
                    <span className="ui-row__trail">
                      <Button variant={a.archived ? 'quiet' : 'secondary'} icon={Download} loading={busyId === a.id} data-testid="phone-download"
                        aria-label={`Download ${clean(a.fileName) || 'the video'}`} onClick={() => download(a)}>Download</Button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
      </section>
      <p className="ui-p ui-dense ui-text-3" data-testid="phone-note">Editing needs a window at least 1024 px wide.</p>
    </div>
  );
}
